import { useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOHead from "../../components/feature/SEOHead";
import { useTheme } from "../../contexts/ThemeContext";

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://trackly.app";
const securityJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Security at Trackly — How We Protect Your Data",
  "description": "Trackly protects your flip data with AES-256 encryption, TLS 1.3, 2FA, row-level security, and SOC 2 certified infrastructure.",
  "url": `${SITE_URL}/security`,
  "isPartOf": { "@type": "WebSite", "name": "Trackly", "url": SITE_URL },
};

const PILLARS = [
  {
    icon: "ri-lock-password-line",
    title: "Encryption at Rest & in Transit",
    body: "All data stored in Trackly is encrypted at rest using AES-256. Every connection between your browser and our servers uses TLS 1.3. Your data is never transmitted or stored in plaintext.",
  },
  {
    icon: "ri-shield-keyhole-line",
    title: "Authentication & 2FA",
    body: "Trackly uses industry-standard authentication via Supabase Auth. Two-factor authentication (2FA) via email OTP is available and recommended for all accounts. Session tokens are short-lived and rotated automatically.",
  },
  {
    icon: "ri-server-line",
    title: "Infrastructure Security",
    body: "Trackly runs on SOC 2 Type II certified infrastructure. Our database is isolated behind a private VPC with no public internet access. Automated backups run daily and are stored in a separate geographic region.",
  },
  {
    icon: "ri-shield-user-line",
    title: "Row-Level Security",
    body: "Every database query is governed by Row-Level Security (RLS) policies. This means users can only ever read or write their own data — no cross-account data leakage is architecturally possible.",
  },
  {
    icon: "ri-eye-off-line",
    title: "No Data Sharing or Advertising",
    body: "Your financial data is never sold, shared with advertisers, or used for any purpose other than running your Trackly account. We have no ad-based business model — ever.",
  },
  {
    icon: "ri-git-repository-private-line",
    title: "Secret Management",
    body: "API keys and secrets are stored in isolated environment vaults and injected at runtime via Supabase Edge Function secrets. They are never committed to source code or exposed client-side.",
  },
];

const PRACTICES = [
  { icon: "ri-bug-line", label: "Security vulnerabilities are triaged within 48 hours" },
  { icon: "ri-refresh-line", label: "Dependencies are reviewed and updated monthly" },
  { icon: "ri-file-shield-2-line", label: "Code changes go through peer review before deployment" },
  { icon: "ri-alert-line", label: "Automated vulnerability scanning on every build" },
  { icon: "ri-time-line", label: "Incident response plan with < 4hr notification SLA for critical issues" },
  { icon: "ri-global-line", label: "Penetration testing performed annually by a third party" },
];

export default function Security() {
  const { isDark } = useTheme();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-[#050505]" : "bg-[#f8f8f8]"}`}>
      <SEOHead
        title="Security at Trackly — How We Protect Your Reseller Data"
        description="Trackly protects your flip data with AES-256 encryption, TLS 1.3, 2FA, row-level security, and SOC 2 certified infrastructure. Your data stays yours."
        keywords="trackly security, reseller data encryption, flip tracker 2FA, profit tracker security"
        canonical={`${SITE_URL}/security`}
        jsonLd={securityJsonLd}
      />
      <Navbar />

      <main className="pt-32 pb-24 px-8 lg:px-16">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="mb-16">
            <span className={`inline-flex items-center gap-2 font-inter text-xs font-medium px-3 py-1.5 rounded-full mb-6 ${isDark ? "bg-white/5 text-[#888]" : "bg-black/6 text-[#666]"}`}>
              <i className="ri-shield-check-line text-xs" />
              Last updated March 20, 2026
            </span>
            <h1 className={`font-syne text-4xl font-bold mb-5 leading-tight ${isDark ? "text-white" : "text-[#111111]"}`}>
              Security
            </h1>
            <p className={`font-inter text-base leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
              Your flip data is sensitive. We take security seriously at every layer — from encryption and authentication to infrastructure and access control.
            </p>
          </div>

          {/* Security Pillars */}
          <section className="mb-10">
            <h2 className={`font-syne text-xl font-semibold mb-7 ${isDark ? "text-white" : "text-[#111111]"}`}>
              How We Protect Your Data
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {PILLARS.map((pillar) => (
                <div
                  key={pillar.title}
                  className={`rounded-2xl border p-6 transition-colors duration-300 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl mb-5 ${isDark ? "bg-white/6" : "bg-black/5"}`}>
                    <i className={`${pillar.icon} text-base ${isDark ? "text-[#aaaaaa]" : "text-[#555555]"}`} />
                  </div>
                  <h3 className={`font-inter text-sm font-semibold mb-3 ${isDark ? "text-[#cccccc]" : "text-[#222222]"}`}>
                    {pillar.title}
                  </h3>
                  <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
                    {pillar.body}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Engineering Practices */}
          <section
            className={`rounded-2xl border p-8 mb-10 transition-colors duration-300 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}
          >
            <div className="flex items-center gap-3 mb-7">
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${isDark ? "bg-white/6" : "bg-black/5"}`}>
                <i className={`ri-code-s-slash-line text-base ${isDark ? "text-[#aaaaaa]" : "text-[#555555]"}`} />
              </div>
              <h2 className={`font-syne text-lg font-semibold ${isDark ? "text-white" : "text-[#111111]"}`}>
                Engineering Practices
              </h2>
            </div>
            <ul className="flex flex-col gap-4">
              {PRACTICES.map((p) => (
                <li key={p.label} className="flex items-start gap-3">
                  <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? "text-[#555555]" : "text-[#aaaaaa]"}`}>
                    <i className={`${p.icon} text-sm`} />
                  </div>
                  <span className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
                    {p.label}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* Responsible Disclosure */}
          <section
            className={`rounded-2xl border p-8 transition-colors duration-300 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${isDark ? "bg-white/6" : "bg-black/5"}`}>
                <i className={`ri-error-warning-line text-base ${isDark ? "text-[#aaaaaa]" : "text-[#555555]"}`} />
              </div>
              <h2 className={`font-syne text-lg font-semibold ${isDark ? "text-white" : "text-[#111111]"}`}>
                Responsible Disclosure
              </h2>
            </div>
            <p className={`font-inter text-sm leading-relaxed mb-5 ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
              Found a security vulnerability in Trackly? We appreciate responsible disclosure. Please email us with a clear description of the issue — include steps to reproduce if possible. We will respond within 48 hours and keep you updated throughout the remediation process.
            </p>
            <p className={`font-inter text-sm leading-relaxed mb-6 ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
              We ask that you do not publicly disclose vulnerabilities until we have had a reasonable time to investigate and patch them. We will credit researchers who help us improve Trackly&apos;s security.
            </p>
            <a
              href="mailto:security@trackly.app"
              className={`inline-flex items-center gap-2 font-inter text-sm font-medium cursor-pointer transition-colors duration-200 ${isDark ? "text-white hover:text-[#cccccc]" : "text-[#111111] hover:text-[#444444]"}`}
            >
              <i className="ri-mail-line" />
              security@trackly.app
            </a>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
}
